import _ from 'lodash';

import * as React from 'react';
import { memo } from 'react';

import i18n from 'i18n-js';
import { Form, Icon } from 'semantic-grab-ui/src';
import FormInputWithError from 'omega/components/form/FormInputWithError';
import DynamicHeightFormSelect from 'omega-rtc/components/form/DynamicHeightFormSelect';
import { getCityOptions, getConditionsFlag, getTaxyTypeOptions } from 'omega-rtc/utils/new-trident-utils';
import deleteIcon from 'img/delete_icon.svg';
import { TridentCampaignFormData } from 'omega-rtc/types/ucd/campaign';
import {
  FIELD_IS_ALL_CITY_SELECTED,
  FIELD_IS_ALL_TAXI_SELECTED,
  FIELD_MANDATORY_CONDITION,
  FIELD_SELECTED_CITY,
  FIELD_SELECTED_TAXI,
  getMandatoryConditionFields,
} from 'omega-rtc/utils/simple-scenario-utils';


type Props = {
  eventNoun: string,
  country: any,
  formData: TridentCampaignFormData,
  handleFormDataChange: Function,
  handleDeleteEvent: Function,
  validateFormRef: Function,
  isEditPage: boolean,
  errors: Array<string>
};

export class EventSection extends React.Component {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.props.validateFormRef(this.validateForm);
  }

  componentDidMount() {
    const mandatoryCondition = _.cloneDeep(_.get(this.props.formData, FIELD_MANDATORY_CONDITION, {}));
    const { country, isEditPage } = this.props;
    const cities = _.get(country, 'cities', []);
    let selectedCityIds = _.get(mandatoryCondition, FIELD_SELECTED_CITY, []);
    const selectedTaxiTypeIDs = _.get(mandatoryCondition, FIELD_SELECTED_TAXI, []);


    // Currently, we do not store the fields in the database if user selects all cities or all taxi types.
    // Therefore, if on edit page these fields are empty, it is safe to assume that the user selected all cities during creation.
    // In this case, we can set the value of the field to 'SelectAll'.
    if (isEditPage) {
      if (selectedCityIds.length === 0) {
        selectedCityIds = cities.map(city => city.cityID);
        mandatoryCondition.isAllCitiesSelected = true;
        mandatoryCondition.selectedPickupCities = selectedCityIds;
      }


      // Set default value to 'Select All' for taxi types condition
      if (selectedTaxiTypeIDs.length === 0) {
        const taxiTypeIDsOptions = getTaxyTypeOptions(cities, selectedCityIds, this.handleAllTaxiTypesSelected);
        const taxiTypeIds = taxiTypeIDsOptions.map(taxiTypeOption => taxiTypeOption.value);
        mandatoryCondition.isAllTaxiTypesSelected = true;
        mandatoryCondition.selectedTaxiTypeIDs = taxiTypeIds;
      }
    }

    this.props.handleFormDataChange({ mandatoryCondition });
  }

  setMandatoryCondition(field: string, value: string, flagField: string, flagValue: boolean) {
    const formData = _.cloneDeep(this.props.formData);
    const mandatoryCondition = _.get(formData, FIELD_MANDATORY_CONDITION, {});
    _.set(mandatoryCondition, field, value);
    _.set(mandatoryCondition, flagField, flagValue);
    this.props.handleFormDataChange({ mandatoryCondition }
    );
  }

  // Taxi Types Condition
  handleTaxiTypeIdsChange = (event: any, data: any) => {
    this.setMandatoryCondition(
      FIELD_SELECTED_TAXI, data.value,
      FIELD_IS_ALL_TAXI_SELECTED, false
    );
  };

  handleAllTaxiTypesSelected = (taxiTypes: any) => {
    this.setMandatoryCondition(
      FIELD_SELECTED_TAXI, _.map(taxiTypes, 'value'),
      FIELD_IS_ALL_TAXI_SELECTED, true
    );
  };

  handleClearTaxiTypeIDs = () => {
    this.setMandatoryCondition(
      FIELD_SELECTED_TAXI, [],
      FIELD_IS_ALL_TAXI_SELECTED, false
    );
  };


  // Pick Up City Condition
  handlePickUpCitiesChange = (event: any, data: any) => {
    const selectedPickupCities = data.value;
    const { country, formData, handleFormDataChange } = this.props;

    const mandatoryCondition = _.cloneDeep(_.get(formData, FIELD_MANDATORY_CONDITION, {}));
    const prevPickupCities = _.cloneDeep(_.get(mandatoryCondition, FIELD_SELECTED_CITY, []));

    _.set(mandatoryCondition, FIELD_SELECTED_CITY, selectedPickupCities);
    _.set(mandatoryCondition, FIELD_IS_ALL_CITY_SELECTED, false);

    // If a cityID is removed, remove the taxiIDs that belong to that removed city.
    if (selectedPickupCities.length < prevPickupCities.length) {
      const selectedTaxiIds = _.get(mandatoryCondition, FIELD_SELECTED_TAXI, []);

      // If no taxi types are selected , nothing to do
      if (_.isEmpty(selectedTaxiIds)) {
        handleFormDataChange({ mandatoryCondition });
        return;
      }

      // Find available taxi IDs
      const selectedCitiesObject = _.filter(country.cities, city => selectedPickupCities.includes(city.cityID));
      const availableTaxiIds = _.flatten(selectedCitiesObject.map(city => city.taxiTypes.map(taxiType => taxiType.taxiTypeID)));

      // Remove any selected taxi IDs that are not available after removing the city
      const updatedSelectedTaxiIds = selectedTaxiIds.filter(taxiId => availableTaxiIds.includes(taxiId));
      _.set(mandatoryCondition, FIELD_SELECTED_TAXI, updatedSelectedTaxiIds);
    }

    handleFormDataChange({ mandatoryCondition });
  };


  handleAllCitiesSelected = () => {
    this.setMandatoryCondition(
      FIELD_SELECTED_CITY, _.map(this.props.country.cities, 'cityID'),
      FIELD_IS_ALL_CITY_SELECTED, true
    );
  };

  handleClearCities = () => {
    this.setMandatoryCondition(
      FIELD_SELECTED_CITY, [],
      FIELD_IS_ALL_CITY_SELECTED, false
    );
  };


  validateForm = () => {
    const { ruleTypes, formData } = this.props;
    const mandatoryCondition = _.get(formData, FIELD_MANDATORY_CONDITION);

    let errors = [];
    const { needCityID, needServiceTypeID } = getConditionsFlag(ruleTypes);
    if (needCityID && _.isEmpty(_.get(mandatoryCondition, FIELD_SELECTED_CITY, []))) {
      errors = [...errors, "City can't be blank"];
    }
    if (needServiceTypeID && _.isEmpty(_.get(mandatoryCondition, FIELD_SELECTED_TAXI, []))) {
      errors = [...errors, "Service Type ID can't be blank"];
    }
    return errors;
  }

  render() {
    const { ruleTypes, eventNoun, country, eventDescription, formData, handleDeleteEvent } = this.props;
    const {
      selectedPickupCities,
      selectedTaxiTypeIDs,
      isAllTaxiTypesSelected,
      isAllCitiesSelected,
      selectedCurrency,
    } = getMandatoryConditionFields(formData);

    const { needCityID, needServiceTypeID, needCurrency } = getConditionsFlag(ruleTypes);

    // Option List
    const cities = _.get(country, 'cities', []);
    const citiesOptions = getCityOptions(cities, this.handleAllCitiesSelected);
    const taxiTypeIDsOptions = getTaxyTypeOptions(cities, selectedPickupCities, this.handleAllTaxiTypesSelected);

    const errors = _.get(this.props, 'errors', []);
    return (
      <React.Fragment>
        <button className={'delete-button'} onClick={handleDeleteEvent}>
          <img src={deleteIcon} height={'17px'} width={'15px'} alt={''} />
        </button>
        <div className={'panel-header'}>
          <Icon name="dropdown" /> <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>{eventNoun}</p>
        </div>
        <p style={{ fontSize: '13px' }}>{eventDescription}</p>

        {!_.isEmpty(errors) &&
          <div className="ui negative message">
            <div className="header"><span className="validation-icon" />
              {_.map(errors, (error) => <p>{error}</p>)}
            </div>
          </div>
        }

        {/* Mandatory Condition */}
        {needCurrency &&
          <div className={'event-condition'}>
            <span className={'operator'}>AND</span>
            <span className={'field-name'}>{i18n.t('targets_page.fields.currency')}</span> <span
              className={'red-asterisk'}
            >*</span>
            <Form.Field>
              <FormInputWithError
                name={'currency'}
                {...this.props}
              >
                <Form.Input
                  required
                  value={selectedCurrency}
                  disabled
                />
              </FormInputWithError>
            </Form.Field>
          </div>
        }

        {/* City ID */}
        {needCityID &&
          <div className={'event-condition'}>
            <label className={'operator'}>AND</label>
            <label className={'field-name'}>
              {i18n.t('promotions_page.fields.city')} <span className={'red-asterisk'}>*</span>
              {!_.isEmpty(selectedPickupCities) && (
                <React.Fragment>
                  <span className="number-of-item-selected"> ({selectedPickupCities.length} Selected) </span>
                  <a onClick={this.handleClearCities}>
                    Clear all
                  </a>
                </React.Fragment>)
              }
            </label>
            <Form.Field>
              <FormInputWithError
                name={'pickupCities'}
                {...this.props}
              >
                <DynamicHeightFormSelect
                  multiple
                  options={citiesOptions}
                  value={isAllCitiesSelected ? [0] : selectedPickupCities}
                  onChange={this.handlePickUpCitiesChange}
                  // onBlur={this.props.handleControlBlur} TODO: Find out what is this handler does
                />
              </FormInputWithError>
            </Form.Field>

          </div>
        }
        {needServiceTypeID &&
          <div className={'event-condition'}>
            <label className={'operator'}>AND</label>
            <label className={'field-name'}>{i18n.t('promotions_page.fields.eligibility.taxi_types_ids')} <span
              className={'red-asterisk'}
            >*</span>
            {!_.isEmpty(selectedTaxiTypeIDs) && (<React.Fragment>
              <span className={'number-of-item-selected'}> ({selectedTaxiTypeIDs.length} Selected) </span>
              <a onClick={this.handleClearTaxiTypeIDs}>
                    Clear all
              </a>
            </React.Fragment>
            )}
            </label>
            <Form.Field>
              <FormInputWithError
                name={'pickupCities'}
                {...this.props}
              >
                <DynamicHeightFormSelect
                  id="taxi-types-ids-tags"
                  search
                  multiple
                  selection
                  allowAdditions
                  options={taxiTypeIDsOptions}
                  value={isAllTaxiTypesSelected ? [0] : selectedTaxiTypeIDs}
                  onChange={this.handleTaxiTypeIdsChange}
                  // onBlur={this.props.handleControlBlur} TODO: Find out what the method for
                />
              </FormInputWithError>
            </Form.Field>
          </div>
        }
      </React.Fragment>
    );
  }
}

// This page is taking very long to render, wrap it memo to only rerender when related data is changed.
export default memo((props) => {
  return <EventSection {...props} />;
}, (prevProps, nextProps) => {
  const conditionField = `formData.${FIELD_MANDATORY_CONDITION}`;
  const errorField = 'errors';
  return _.get(prevProps, conditionField, {}) === _.get(nextProps, conditionField, {}) &&
    _.get(prevProps, errorField, []) === _.get(nextProps, errorField, []); // only re-render if mandatory condition props changes
});
